import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core'
import { E11DynamicFormBuilderComponent, e11FieldInputCheckBox, e11FormFor, e11FormLayoutInlineGrid } from '@engineering11/ui-lib/e11-form-builder'
import { ICheckboxOptions } from '@engineering11/ui-lib/e11-input-checkbox'
import { arrayToObject, isDeepEqual } from '@engineering11/utility'
import { APPLICATION_STATE, SwimlaneWatchConfig, applicationStateMappings } from 'shared-lib'

type SwimlaneNamesFormType = SwimlaneWatchConfig

const stateToLabel: Record<APPLICATION_STATE, string> = {
  [APPLICATION_STATE.APPLIED]: 'Applicant has applied for the position. No screening or communications have taken place.',
  [APPLICATION_STATE.SCREENED]: 'Applicant has been screened or has met the requirements to be considered for the position.',
  [APPLICATION_STATE.INTERVIEWING]: 'Applicant is actively being interviewed.',
  [APPLICATION_STATE.FINALIST]: 'Applicant is a finalist for the position.',
  [APPLICATION_STATE.OFFER_SENT]: 'An offer has been made and sent to the applicant.',
  [APPLICATION_STATE.HIRED]: 'An offer has been accepted by the applicant.',
  [APPLICATION_STATE.WITHDRAWN]: 'Applicant has withdrawn from consideration.',
  [APPLICATION_STATE.NOT_INTERESTED]: 'Applicant has been rejected by the Decision Team for this position.',
  [APPLICATION_STATE.UNRESPONSIVE]: 'Applicant is unresponsive.',
}
const stateToSelectOption = (state: APPLICATION_STATE): ICheckboxOptions => ({
  name: applicationStateMappings.get(state)!,
  value: state,
  checkboxProps: {
    // The theme's face has no 700 weight, so `e11-font-bold` was synthesised by the browser —
    // a smeared pseudo-bold beside the library's extralight description. This is the real
    // demibold face the rest of the app labels with.
    labelClassOverrides: 'e11-font-primary-demibold',
    description: stateToLabel[state],
    descriptionClassOverrides: 'e11-ml-6 e11-text-sm',
  },
})

const ORDERED_STATES = [
  APPLICATION_STATE.APPLIED,
  APPLICATION_STATE.SCREENED,
  APPLICATION_STATE.INTERVIEWING,
  APPLICATION_STATE.FINALIST,
  APPLICATION_STATE.OFFER_SENT,
  APPLICATION_STATE.HIRED,
  APPLICATION_STATE.UNRESPONSIVE,
  APPLICATION_STATE.NOT_INTERESTED,
  APPLICATION_STATE.WITHDRAWN,
]

const checkboxes = Object.values(ORDERED_STATES).map(state =>
  e11FieldInputCheckBox(state, '', [stateToSelectOption(state)], undefined, {
    extraInputOption: {},
  })
)

@Component({
  selector: 'swimlane-watch-config-form',
  styles: [
    `
      /* The library hardcodes e11-font-extralight (200) and e11-text-skin-base on the checkbox
         description — another synthesised weight, at the same colour as the label above it.
         Both are restated here rather than through descriptionClassOverrides, since a utility
         appended to the same class list would be settled by stylesheet order, not by us. The
         option now reads as a label with a sentence under it. */
      :host ::ng-deep [id$='-description'] {
        font-weight: 400;
        color: rgb(var(--color-text-light));
      }
    `,
  ],
  template: `
    <e11-dynamic-form-builder
      #form
      [initialValue]="_initialValue"
      [fieldGroups]="fieldGroups"
      (valueChanges)="onValueChange($event)"
      (onSubmit)="onSave($event)"
      [hideSubmitMenu]="!showSaveButton"
      [loading]="loading"
      [disabled]="!isEditable"
    ></e11-dynamic-form-builder>
  `,
})
export class SwimlaneWatchConfigFormComponent {
  @ViewChild('form') form!: E11DynamicFormBuilderComponent<SwimlaneNamesFormType>

  @Input() set initialValue(initialValue: APPLICATION_STATE[] | undefined) {
    this._initialValue = this.checkPossibleValues(Object.values(APPLICATION_STATE), initialValue ?? [])
  }

  @Input() isEditable = true

  @Input() loading = false
  @Input() showSaveButton = true
  @Output() watchConfigUpdated = new EventEmitter<APPLICATION_STATE[]>()
  @Output() saveClicked = new EventEmitter<APPLICATION_STATE[]>()

  _initialValue: SwimlaneWatchConfig = arrayToObject(Object.values(APPLICATION_STATE), () => false)

  fieldGroups = e11FormFor<SwimlaneNamesFormType>()([
    e11FormLayoutInlineGrid(checkboxes, {
      // items-start, not items-center: the options have descriptions of different lengths, and
      // centring each cell left the checkboxes on a ragged line down the column.
      containerClass: 'e11-grid e11-grid-cols-1 e11-items-start e11-justify-center md:e11-grid-cols-2 e11-gap-x-8 e11-gap-y-5 e11-mb-8',
    }),
  ])

  constructor() {}

  onValueChange(output: SwimlaneWatchConfig) {
    if (isDeepEqual(output, this._initialValue)) return
    const swimlanes = Object.keys(output).filter(key => output[key as APPLICATION_STATE])
    this.watchConfigUpdated.emit(swimlanes as APPLICATION_STATE[])
  }

  onSave(output: SwimlaneWatchConfig) {
    const swimlanes = Object.keys(output).filter(key => output[key as APPLICATION_STATE])
    this.saveClicked.emit(swimlanes as APPLICATION_STATE[])
  }

  checkPossibleValues<T extends string | number>(possibleValues: T[], actualValues: T[]) {
    const result: Record<T, boolean> = {} as Record<T, boolean>

    possibleValues.forEach(possibleValue => {
      result[possibleValue] = actualValues.includes(possibleValue)
    })

    return result
  }
}
